import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUpRight, Move3D } from "lucide-react";
import { usePreferences } from "../PreferencesContext";
import { cn } from "../utils/cn";
import { eyebrow, primaryButton, quietButton, sectionFrame } from "../styles";

const Earth = lazy(() => import("./Earth"));

const Herosection = () => {
  const [earthEnabled, setEarthEnabled] = useState(false);
  const earthStageRef = useRef(null);
  const { copy } = usePreferences();
  const text = copy.hero;

  useEffect(() => {
    const stage = earthStageRef.current;
    if (!stage) return undefined;

    const observer = typeof IntersectionObserver === "undefined"
      ? null
      : new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setEarthEnabled(true);
            observer?.disconnect();
          }
        },
        { rootMargin: "180px", threshold: 0.01 },
      );

    if (observer) observer.observe(stage);
    else setEarthEnabled(true);

    return () => {
      observer?.disconnect();
    };
  }, []);

  return (
    <section className={cn("hero flex min-h-dvh items-center bg-[var(--page)] pt-[5rem] pb-8 transition-colors duration-200 max-[900px]:items-start max-[900px]:pt-32 max-[720px]:pt-[8.5rem]", sectionFrame)} id="home" aria-labelledby="hero-heading">
      <div className="grid min-h-[calc(100dvh-8.5rem)] grid-cols-[minmax(0,0.92fr)_minmax(29rem,1.08fr)] items-center gap-[clamp(2rem,5vw,5rem)] max-[1080px]:grid-cols-[minmax(0,0.95fr)_minmax(25rem,1.05fr)] max-[1080px]:gap-10 max-[900px]:min-h-0 max-[900px]:grid-cols-1 max-[900px]:gap-16">
        <div>
          <p className={eyebrow}>{text.eyebrow}</p>
          <h1 className="mt-[1.6rem] max-w-[11ch] text-balance text-[clamp(3.5rem,6.4vw,6.8rem)] leading-[0.95] font-[670] tracking-[-0.075em] max-[900px]:max-w-[12ch] max-[720px]:text-[clamp(3rem,13vw,4.6rem)] fa:max-w-[12ch] fa:text-[clamp(3.1rem,5.5vw,5.8rem)] fa:leading-[1.04] fa:font-[720] fa:tracking-normal [&_em]:font-[630] [&_em]:not-italic [&_em]:text-[var(--muted)]" id="hero-heading">
            {text.title} <em>{text.emphasis}</em>
          </h1>
          <p className="mt-[1.7rem] max-w-[41rem] text-pretty text-[clamp(1.05rem,1.5vw,1.24rem)] leading-[1.62] tracking-[-0.015em] text-[var(--muted)] fa:leading-[1.9] fa:tracking-normal">{text.lede}</p>

          <div className="mt-8 flex flex-wrap gap-3 max-[420px]:flex-col max-[420px]:items-stretch">
            <a className={primaryButton} href="#projects">
              {text.work}
              <ArrowDown aria-hidden="true" />
            </a>
            <a className={quietButton} href="#contact">
              {text.contact}
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>

          <div className="mt-[clamp(2.75rem,5vw,4.5rem)] grid grid-cols-3 gap-4 border-t border-[var(--line)] pt-[1.2rem] max-[720px]:grid-cols-2 max-[420px]:gap-3 [&>div:last-child]:max-[720px]:col-span-full" aria-label={text.proofLabel}>
            {text.proof.map(([title, detail]) => <div className="flex min-w-0 flex-col gap-[0.35rem]" key={title}><strong className="text-[0.72rem] font-[650] text-[var(--ink)]">{title}</strong><span className="text-[0.72rem] leading-[1.4] text-[var(--subtle)]">{detail}</span></div>)}
          </div>
        </div>

        <div className="flex min-w-0 self-stretch items-center justify-center pb-[4.5rem] max-[900px]:mx-auto max-[900px]:w-[min(100%,42rem)] max-[900px]:pb-0">
          <div ref={earthStageRef} className="earth-stage relative isolate aspect-square w-full max-w-[min(46rem,78dvh)] overflow-visible">
            <div className="pointer-events-none absolute inset-[2%] -z-10 rounded-full bg-[image:var(--earth-glow)]" aria-hidden="true" />
            {earthEnabled ? (
              <Suspense
                fallback={(
                  <div className="absolute inset-0 z-10 grid place-items-center overflow-hidden" role="status" aria-label={text.earthLoading}>
                    <picture className="contents">
                      <source media="(max-width: 900px)" srcSet="/planet/textures/earth-preview-256.webp" />
                      <img className="aspect-square w-[58%] rounded-full object-cover shadow-[0_2rem_5rem_rgba(43,78,119,0.22)] [filter:saturate(0.82)_contrast(1.05)]" src="/planet/textures/earth-preview-384.webp" alt="" aria-hidden="true" width="384" height="384" />
                    </picture>
                  </div>
                )}
              >
                <Earth label={text.earthLabel} />
              </Suspense>
            ) : (
              <button
                className="absolute inset-0 z-10 grid cursor-grab place-items-center overflow-hidden rounded-full border-0 bg-transparent p-0 focus-visible:outline-offset-[-0.5rem]"
                type="button"
                aria-label={text.earthActivate}
                onClick={() => setEarthEnabled(true)}
                onFocus={() => setEarthEnabled(true)}
              >
                <picture className="contents">
                  <source media="(max-width: 900px)" srcSet="/planet/textures/earth-preview-256.webp" />
                  <img className="aspect-square w-[58%] rounded-full object-cover shadow-[0_2rem_5rem_rgba(43,78,119,0.22)] [filter:saturate(0.82)_contrast(1.05)]" src="/planet/textures/earth-preview-384.webp" alt="" aria-hidden="true" width="384" height="384" fetchPriority="high" />
                </picture>
              </button>
            )}
            <div className="earth-stage__label pointer-events-none absolute top-5 left-5 z-40 flex flex-col gap-1 rounded-2xl border border-white/75 bg-white/60 px-4 py-[0.85rem] text-[var(--ink)] shadow-[0_0.8rem_2rem_rgba(43,78,119,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-[20px] backdrop-saturate-[1.6] rtl:right-5 rtl:left-auto max-[720px]:top-[0.8rem] max-[720px]:left-[0.8rem] rtl:max-[720px]:right-[0.8rem] rtl:max-[720px]:left-auto">
              <span className="text-[0.76rem] font-[650]">{text.earthTop}</span>
              <small className="text-[0.66rem] text-[var(--muted)] max-[420px]:hidden">{text.earthMeta}</small>
            </div>
            <div className="earth-stage__label pointer-events-none absolute right-5 bottom-5 z-40 inline-flex items-center gap-2 rounded-full border border-white/75 bg-white/60 px-[0.9rem] py-[0.72rem] text-[0.7rem] font-semibold text-[#424245] shadow-[0_0.8rem_2rem_rgba(43,78,119,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-[20px] backdrop-saturate-[1.6] rtl:right-auto rtl:left-5 max-[720px]:right-[0.8rem] max-[720px]:bottom-[0.8rem] rtl:max-[720px]:right-auto rtl:max-[720px]:left-[0.8rem]">
              <Move3D className="w-[0.95rem] text-[var(--blue)]" aria-hidden="true" />
              <span>{text.earthDrag}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Herosection;
